import { useEffect, useState } from "react";
import {
    Plus,
    X,
    Edit3,
    Trash2,
    Search,
    Upload,
    Download,
    ChevronLeft,
    ChevronRight,
    Truck,
    MapPin,
    User,
    Phone,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileText,
} from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

// Mock API Base URL - replace with your actual config
import API_BASE_URL from "../config";

export default function ShippingInformation({ setCurrentPage }) {
    const [shipping, setShipping] = useState([]);
    const [filtered, setFiltered] = useState([]);

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedShipping, setSelectedShipping] = useState(null);

    const [formData, setFormData] = useState({
        shipTo_Location: "",
        shipTo_Address1: "",
        shipTo_Address2: "",
        shipTo_Address3: "",
        shipTo_ContactNo: "",
        shipTo_ContactName: "",
        isActive: true,
        id: 0
    });

    const [search, setSearch] = useState("");

    // Import Modal States
    const [showImportModal, setShowImportModal] = useState(false);
    const [importData, setImportData] = useState([]);
    const [importMode, setImportMode] = useState("new"); // "new" or "update"

    // Pagination
    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    // ---------------- API FUNCTIONS ---------------- //

    const fetchShipping = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/ShippingInfo`);
            if (!res.ok) throw new Error("Failed to fetch shipping info");

            const data = await res.json();

            // Map to handle API response with underscores
            const mapped = data.map((s) => ({
                id: s.id,
                shipToLocation: s.shipTo_Location || s.Ship_To_Location || s.shipToLocation,
                shipToAddress1: s.shipTo_Address1 || s.Ship_To_Address1 || s.shipToAddress1,
                shipToAddress2: s.shipTo_Address2 || s.Ship_To_Address2 || s.shipToAddress2,
                shipToAddress3: s.shipTo_Address3 || s.Ship_To_Address3 || s.shipToAddress3,
                shipToContactNumber: s.shipTo_ContactNo || s.Ship_To_ContactNumber || s.shipToContactNumber,
                shipToContactName: s.shipTo_ContactName || s.Ship_To_ContactName || s.shipToContactName,
                isActive: s.isActive === 1 || s.isActive === true || s.IsActive === true,
                createdAt: s.createdAt || s.created_at,
            }));

            setShipping(mapped);
            setFiltered(mapped);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to fetch shipping info", "error");
        }
    };

    const createShipping = async () => {
        if (!formData.shipTo_Location) {
            Swal.fire("Oops!", "Ship To Location is required", "warning");
            return;
        }

        try {
            const formDataObj = new FormData();
            formDataObj.append("ShipTo_Location", formData.shipTo_Location);
            formDataObj.append("ShipTo_Address1", formData.shipTo_Address1 || "");
            formDataObj.append("ShipTo_Address2", formData.shipTo_Address2 || "");
            formDataObj.append("ShipTo_Address3", formData.shipTo_Address3 || "");
            formDataObj.append("ShipTo_ContactNo", formData.shipTo_ContactNo || "");
            formDataObj.append("ShipTo_ContactName", formData.shipTo_ContactName || "");
            formDataObj.append("IsActive", formData.isActive ? 1 : 0);

            const res = await fetch(`${API_BASE_URL}api/ShippingInfo`, {
                method: "POST",
                body: formDataObj,
            });

            if (!res.ok) throw new Error("Failed to create shipping info");

            setOpenCreate(false);
            clearForm();
            fetchShipping();
            Swal.fire("Success!", "Shipping info created successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const updateShipping = async () => {
        if (!formData.shipTo_Location) {
            Swal.fire("Oops!", "Ship To Location is required", "warning");
            return;
        }

        try {
            const res = await fetch(
                `${API_BASE_URL}api/ShippingInfo/${selectedShipping.id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(formData),
                }
            );

            if (!res.ok) throw new Error("Failed to update shipping info");

            setOpenEdit(false);
            setSelectedShipping(null);
            clearForm();
            fetchShipping();
            Swal.fire("Updated!", "Shipping info updated successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const deleteShipping = async (s) => {
        const result = await Swal.fire({
            title: `Are you sure?`,
            text: `You are about to delete ${s.shipToLocation}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch(
                    `${API_BASE_URL}api/ShippingInfo/${s.id}`,
                    { method: "DELETE" }
                );
                if (!res.ok) throw new Error("Failed to delete shipping info");
                fetchShipping();
                Swal.fire("Deleted!", "Shipping info has been deleted.", "success");
            } catch (err) {
                console.error(err);
                Swal.fire("Error", err.message, "error");
            }
        }
    };

    // EXPORT to Excel
    const exportCSV = () => {
        const exportData = shipping.map((s) => ({
            ShipTo_Location: s.shipToLocation,
            ShipTo_Address1: s.shipToAddress1,
            ShipTo_Address2: s.shipToAddress2,
            ShipTo_Address3: s.shipToAddress3,
            ShipTo_ContactName: s.shipToContactName,
            ShipTo_ContactNo: s.shipToContactNumber,
            IsActive: s.isActive ? 1 : 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ShippingInfo");
        XLSX.writeFile(workbook, "shipping_info.xlsx");
    };

    // IMPORT - STEP 1: Parse and Show Modal
    const handleImportFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
                Swal.fire("Error", "No valid data found in Excel file", "error");
                return;
            }

            // Check for duplicates
            const dataWithStatus = jsonData.map((item) => {
                const existing = shipping.find((s) => {
                    const locationMatch =
                        s.shipToLocation?.toLowerCase().trim() ===
                        item.ShipTo_Location?.toLowerCase().trim();

                    const address1Match =
                        item.ShipTo_Address1 &&
                        s.shipToAddress1?.toLowerCase().trim() ===
                        item.ShipTo_Address1?.toLowerCase().trim();

                    return locationMatch || address1Match;
                });

                return {
                    ...item,
                    isDuplicate: !!existing,
                    existingId: existing?.id,
                    existingData: existing,
                    duplicateReason: existing
                        ? (existing.shipToLocation?.toLowerCase().trim() === item.ShipTo_Location?.toLowerCase().trim()
                            ? "location"
                            : "address")
                        : null
                };
            });

            setImportData(dataWithStatus);
            setShowImportModal(true);
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to parse Excel file", "error");
        }

        // Reset file input
        e.target.value = "";
    };

    // IMPORT - STEP 2: Process Import
    const processImport = async () => {
        setShowImportModal(false);

        // Show loading
        Swal.fire({
            title: 'Importing...',
            text: 'Please wait while we process your data',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            let successCount = 0;
            let failCount = 0;
            const errors = [];

            for (const item of importData) {
                if (item.isDuplicate && importMode === "new") {
                    continue; // Skip duplicates if mode is "new"
                }

                // Map Excel data to API format
                const payload = new FormData();
                payload.append("ShipTo_Location", item.ShipTo_Location || "");
                payload.append("ShipTo_Address1", item.ShipTo_Address1 || "");
                payload.append("ShipTo_Address2", item.ShipTo_Address2 || "");
                payload.append("ShipTo_Address3", item.ShipTo_Address3 || "");
                payload.append("ShipTo_ContactName", item.ShipTo_ContactName || "");
                payload.append("ShipTo_ContactNo", item.ShipTo_ContactNo || "");
                payload.append("IsActive", item.IsActive ?? 1);

                try {
                    if (item.isDuplicate && importMode === "update") {
                        // Update existing - using JSON for PUT
                        const jsonPayload = {
                            shipTo_Location: item.ShipTo_Location || "",
                            shipTo_Address1: item.ShipTo_Address1 || "",
                            shipTo_Address2: item.ShipTo_Address2 || "",
                            shipTo_Address3: item.ShipTo_Address3 || "",
                            shipTo_ContactName: item.ShipTo_ContactName || "",
                            shipTo_ContactNo: item.ShipTo_ContactNo || "",
                            isActive: item.IsActive ?? true,
                        };

                        const res = await fetch(
                            `${API_BASE_URL}api/ShippingInfo/${item.existingId}`,
                            {
                                method: "PUT",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify(jsonPayload),
                            }
                        );

                        if (res.ok) {
                            successCount++;
                        } else {
                            failCount++;
                            const errText = await res.text();
                            errors.push(`${item.ShipTo_Location}: ${errText}`);
                        }
                    } else {
                        // Create new - using FormData
                        const res = await fetch(`${API_BASE_URL}api/ShippingInfo`, {
                            method: "POST",
                            body: payload,
                        });

                        if (res.ok) {
                            successCount++;
                        } else {
                            failCount++;
                            const errText = await res.text();
                            errors.push(`${item.ShipTo_Location}: ${errText}`);
                        }
                    }
                } catch (err) {
                    failCount++;
                    errors.push(`${item.ShipTo_Location}: ${err.message}`);
                }
            }

            fetchShipping();
            setImportData([]);
            setImportMode("new");

            // Show detailed results
            Swal.close();

            if (failCount > 0) {
                Swal.fire({
                    icon: "warning",
                    title: "Import Completed with Errors",
                    html: `
                        <p class="text-lg mb-2"><strong>${successCount}</strong> records processed successfully</p>
                        <p class="text-red-600 mb-3"><strong>${failCount}</strong> failed</p>
                        <div class="text-left text-sm max-h-40 overflow-y-auto bg-gray-50 p-3 rounded">
                            <p class="font-semibold mb-2">Errors:</p>
                            ${errors.slice(0, 10).map(e => `<p class="text-red-600 text-xs">• ${e}</p>`).join('')}
                            ${errors.length > 10 ? `<p class="text-gray-500 text-xs mt-2">...and ${errors.length - 10} more errors</p>` : ''}
                        </div>
                    `,
                    width: '600px'
                });
            } else {
                Swal.fire({
                    icon: "success",
                    title: "Import Complete!",
                    html: `<p class="text-lg"><strong>${successCount}</strong> records processed successfully</p>`,
                });
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to import shipping info", "error");
        }
    };

    // ---------------- USE EFFECTS ---------------- //

    useEffect(() => {
        fetchShipping();
    }, []);

    useEffect(() => {
        if (!search.trim()) {
            setFiltered(shipping);
        } else {
            const term = search.toLowerCase();
            setFiltered(
                shipping.filter(
                    (s) =>
                        s.shipToLocation?.toLowerCase().includes(term) ||
                        s.shipToContactName?.toLowerCase().includes(term)
                )
            );
        }
        setPage(1);
    }, [search, shipping]);

    // ---------------- HELPERS ---------------- //

    const clearForm = () => {
        setFormData({
            shipTo_Location: "",
            shipTo_Address1: "",
            shipTo_Address2: "",
            shipTo_Address3: "",
            shipTo_ContactNo: "",
            shipTo_ContactName: "",
            isActive: true,
        });
    };

    const openEditModal = (s) => {
        setSelectedShipping(s);
        setFormData({
            shipTo_Location: s.shipToLocation,
            shipTo_Address1: s.shipToAddress1,
            shipTo_Address2: s.shipToAddress2,
            shipTo_Address3: s.shipToAddress3,
            shipTo_ContactNo: s.shipToContactNumber,
            shipTo_ContactName: s.shipToContactName,
            isActive: s.isActive,
        });
        setOpenEdit(true);
    };

    const duplicateCount = importData.filter((d) => d.isDuplicate).length;
    const newCount = importData.filter((d) => !d.isDuplicate).length;

    // ---------------- RETURN UI ---------------- //
    return (
        <div className="min-h-screen from-gray-50 to-gray-100 p-8">
            {/* BACK BUTTON */}
            <button
                onClick={() => setCurrentPage && setCurrentPage("referencess")}
                className="mb-2 flex items-center gap-5 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
            >
                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Back to References
            </button>

            {/* HEADER */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <Truck className="text-indigo-600" size={36} />
                            Shipping Information
                        </h1>
                        <p className="text-gray-600 mt-2">Manage shipping addresses and contacts</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={exportCSV}
                            className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-5 py-3 rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                        >
                            <Download size={18} /> Export
                        </button>

                        <label className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer">
                            <Upload size={18} /> Import
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={handleImportFile}
                            />
                        </label>

                        <button
                            onClick={() => setOpenCreate(true)}
                            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                        >
                            <Plus size={20} /> Create Shipping Info
                        </button>
                    </div>
                </div>
            </div>

            {/* SEARCH BAR */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by location or contact name..."
                        className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                <th className="px-6 py-4 text-left text-sm font-semibold">Location</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Address</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Contact Info</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginated.length > 0 ? (
                                paginated.map((ship, index) => (
                                    <tr
                                        key={ship.id || `ship-${index}`}
                                        className="hover:bg-indigo-50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                                                    <Truck size={20} />
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-800">
                                                        {ship.shipToLocation}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        ID: {ship.id}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-start gap-2 text-sm text-gray-600">
                                                <MapPin size={16} className="mt-1 text-gray-400 flex-shrink-0" />
                                                <div>
                                                    {ship.shipToAddress1 && <div>{ship.shipToAddress1}</div>}
                                                    {ship.shipToAddress2 && (
                                                        <div className="text-gray-500">{ship.shipToAddress2}</div>
                                                    )}
                                                    {ship.shipToAddress3 && (
                                                        <div className="text-gray-500">{ship.shipToAddress3}</div>
                                                    )}
                                                    {!ship.shipToAddress1 && !ship.shipToAddress2 && !ship.shipToAddress3 && (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1 text-sm">
                                                {ship.shipToContactName && (
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <User size={14} className="text-gray-400" />
                                                        {ship.shipToContactName}
                                                    </div>
                                                )}
                                                {ship.shipToContactNumber && (
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <Phone size={14} className="text-gray-400" />
                                                        {ship.shipToContactNumber}
                                                    </div>
                                                )}
                                                {!ship.shipToContactName && !ship.shipToContactNumber && (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center">
                                                {ship.isActive ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <CheckCircle size={14} />
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        <XCircle size={14} />
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(ship)}
                                                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Edit"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => deleteShipping(ship)}
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-400">
                                            <Truck size={48} />
                                            <p className="text-lg font-medium">No shipping info found</p>
                                            <p className="text-sm">
                                                Try adjusting your search or create new shipping info
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {paginated.length > 0 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                                Showing {(page - 1) * perPage + 1} to{" "}
                                {Math.min(page * perPage, filtered.length)} of {filtered.length}{" "}
                                shipping info
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700">
                                    Page {page} of {totalPages || 1}
                                </span>
                                <button
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* CREATE/EDIT MODAL */}
            {(openCreate || openEdit) && (
                <Modal
                    title={openCreate ? "Create Shipping Info" : "Edit Shipping Info"}
                    close={() => {
                        setOpenCreate(false);
                        setOpenEdit(false);
                        setSelectedShipping(null);
                        clearForm();
                    }}
                >
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                        <Input
                            label="Ship To Location*"
                            value={formData.shipTo_Location}
                            onChange={(val) =>
                                setFormData({ ...formData, shipTo_Location: val })
                            }
                            required
                        />
                        <Input
                            label="Address 1"
                            value={formData.shipTo_Address1}
                            onChange={(val) =>
                                setFormData({ ...formData, shipTo_Address1: val })
                            }
                        />
                        <Input
                            label="Address 2"
                            value={formData.shipTo_Address2}
                            onChange={(val) =>
                                setFormData({ ...formData, shipTo_Address2: val })
                            }
                        />
                        <Input
                            label="Address 3"
                            value={formData.shipTo_Address3}
                            onChange={(val) =>
                                setFormData({ ...formData, shipTo_Address3: val })
                            }
                        />
                        <Input
                            label="Contact Name"
                            value={formData.shipTo_ContactName}
                            onChange={(val) =>
                                setFormData({ ...formData, shipTo_ContactName: val })
                            }
                        />
                        <Input
                            label="Contact Number"
                            value={formData.shipTo_ContactNo}
                            onChange={(val) =>
                                setFormData({ ...formData, shipTo_ContactNo: val })
                            }
                        />
                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) =>
                                    setFormData({ ...formData, isActive: e.target.checked })
                                }
                                id="isActive"
                                className="h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                            />
                            <label
                                htmlFor="isActive"
                                className="text-sm font-semibold text-gray-700 cursor-pointer"
                            >
                                Active
                            </label>
                        </div>
                    </div>

                    <button
                        onClick={openCreate ? createShipping : updateShipping}
                        className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                    >
                        {openCreate ? "Create Shipping Info" : "Update Shipping Info"}
                    </button>
                </Modal>
            )}

            {/* IMPORT PREVIEW MODAL */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-6">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-3xl font-bold flex items-center gap-3">
                                        <FileText size={32} />
                                        Import Preview - Shipping Information
                                    </h2>
                                    <p className="text-indigo-100 mt-2">
                                        Review data before importing • {importData.length} total records
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowImportModal(false);
                                        setImportData([]);
                                        setImportMode("new");
                                    }}
                                    className="text-white hover:bg-white/20 rounded-lg p-2 transition"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                                    <div className="bg-green-100 rounded-full p-3">
                                        <CheckCircle className="text-green-600" size={24} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{newCount}</p>
                                        <p className="text-sm text-gray-600">New Records</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                                    <div className="bg-red-100 rounded-full p-3">
                                        <AlertCircle className="text-red-600" size={24} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{duplicateCount}</p>
                                        <p className="text-sm text-gray-600">Duplicates Found</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mode Selection */}
                        {duplicateCount > 0 && (
                            <div className="px-8 py-4 bg-yellow-50 border-b border-yellow-200">
                                <p className="text-sm font-semibold text-yellow-800 mb-3">
                                    What would you like to do with duplicate records?
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setImportMode("new")}
                                        className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${importMode === "new"
                                                ? "bg-indigo-600 text-white shadow-lg"
                                                : "bg-white text-gray-700 border-2 border-gray-300 hover:border-indigo-300"
                                            }`}
                                    >
                                        Skip Duplicates (Import {newCount} new only)
                                    </button>
                                    <button
                                        onClick={() => setImportMode("update")}
                                        className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${importMode === "update"
                                                ? "bg-indigo-600 text-white shadow-lg"
                                                : "bg-white text-gray-700 border-2 border-gray-300 hover:border-indigo-300"
                                            }`}
                                    >
                                        Update Existing (Import all {importData.length})
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Data Table */}
                        <div className="flex-1 overflow-y-auto px-8 py-4">
                            <table className="w-full">
                                <thead className="sticky top-0 bg-gray-100 border-b-2 border-gray-300">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Location</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Address 1</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Contact Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Contact No</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {importData.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={`${item.isDuplicate
                                                    ? "bg-red-50 hover:bg-red-100"
                                                    : "bg-white hover:bg-gray-50"
                                                } transition-colors`}
                                        >
                                            <td className="px-4 py-3">
                                                {item.isDuplicate ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                                        <AlertCircle size={14} />
                                                        Duplicate
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                        <CheckCircle size={14} />
                                                        New
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`font-medium ${item.isDuplicate ? "text-red-900" : "text-gray-800"}`}>
                                                    {item.ShipTo_Location}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-sm ${item.isDuplicate ? "text-red-700" : "text-gray-600"}`}>
                                                    {item.ShipTo_Address1 || "—"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-sm ${item.isDuplicate ? "text-red-700" : "text-gray-600"}`}>
                                                    {item.ShipTo_ContactName || "—"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-sm ${item.isDuplicate ? "text-red-700" : "text-gray-600"}`}>
                                                    {item.ShipTo_ContactNo || "—"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {item.isDuplicate ? (
                                                    <span className="text-xs text-red-600 font-semibold">
                                                        {importMode === "update" ? "Will Update" : "Will Skip"}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-green-600 font-semibold">
                                                        Will Create
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <button
                                onClick={() => {
                                    setShowImportModal(false);
                                    setImportData([]);
                                    setImportMode("new");
                                }}
                                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={processImport}
                                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                            >
                                {importMode === "update"
                                    ? `Import All (${importData.length} records)`
                                    : `Import New Only (${newCount} records)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Modal Component
function Modal({ title, close, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={close}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
                >
                    <X size={24} />
                </button>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
            />
        </div>
    );
}